import { type PaymentMethod } from '@stripe/stripe-js'
import { createContext, type PropsWithChildren, useContext, useState } from 'react'

export type CardPaymentProviderProps = {
  cardPaymentsEnabled?: boolean;
  stripePublishableKey: string;
}

type CreatePaymentMethod = () => Promise<PaymentMethod | undefined>

type CardPaymentContextType = {
  cardPaymentsEnabled?: boolean;
  stripePublishableKey: string;
  selectedPaymentMethod?: PaymentMethod;
  onPaymentMethodSelected: (paymentMethod: PaymentMethod) => void;
  onRemovePaymentMethod: () => void;
  createPaymentMethod?: CreatePaymentMethod;
  registerCreatePaymentMethod: (createPaymentMethod: CreatePaymentMethod) => void;
  isCreatingPaymentMethod?: boolean;
  setIsCreatingPaymentMethod: (isCreatingPaymentMethod: boolean) => void;
}

const CardPaymentContext = createContext<CardPaymentContextType>({
  cardPaymentsEnabled: false,
  stripePublishableKey: '',
  onPaymentMethodSelected: () => {},
  onRemovePaymentMethod: () => {},
  registerCreatePaymentMethod: () => {},
  setIsCreatingPaymentMethod: () => {},
})

export function useCardPayment () {
  return useContext(CardPaymentContext)
}

export function CardPaymentProvider ({
  cardPaymentsEnabled,
  stripePublishableKey,
  children
}: PropsWithChildren<CardPaymentProviderProps>) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>()
  const [createPaymentMethod, setCreatePaymentMethod] = useState<CreatePaymentMethod>()
  const [isCreatingPaymentMethod, setIsCreatingPaymentMethod] = useState(false)

  return (
    <CardPaymentContext.Provider
      value={{
        cardPaymentsEnabled,
        stripePublishableKey,
        selectedPaymentMethod: paymentMethod,
        onPaymentMethodSelected: setPaymentMethod,
        onRemovePaymentMethod: () => setPaymentMethod(undefined),
        createPaymentMethod,
        registerCreatePaymentMethod: (createPaymentMethod) => setCreatePaymentMethod(() => createPaymentMethod),
        isCreatingPaymentMethod,
        setIsCreatingPaymentMethod,
      }}
    >
      {children}
    </CardPaymentContext.Provider>
  )
}
